import { useEffect } from 'react';
import type { ResolvedSignageConfig } from '@/config/types';

/** Everything `useDocumentTheme` reads. A full config satisfies it. */
export type DocumentTheme = Pick<ResolvedSignageConfig, 'locale' | 'scrollable' | 'layout'>;

/**
 * Applies the config-driven `document` side effects: `<html lang>`, body overflow,
 * background, text colour and className.
 *
 * Replaces the mutations legacy `App.vue` and `Home.vue` performed inline during `setup()`.
 * See docs/MIGRATION.md §3 for the source-to-effect table. Called once, from `App`.
 *
 * Two rules govern this file:
 *
 * 1. **Nothing happens during render.** Mutating `document` while rendering is what makes a
 *    component non-idempotent, and this tree is re-rendered on every MQTT message.
 * 2. **Every effect restores what it found.** In production nothing ever unmounts, so this
 *    looks like dead code — but `StrictMode` double-invokes effects in dev, and an effect
 *    that does not clean up correctly announces itself there as a visible flicker. That is
 *    the point: the bug surfaces on a laptop instead of on a wall.
 */
export function useDocumentTheme(config: DocumentTheme): void {
  const { locale, scrollable } = config;
  const { background, fontColor, class: bodyClass } = config.layout;

  /*
   * `<html lang>` is more than semantics here: legacy `getDateAndTime()` reads it to pick
   * the locale for `toLocaleDateString`, and the widgets in SYM-495 do the same. It must
   * therefore be right before any widget reads a date.
   *
   * React runs child effects before parent effects, so this effect alone cannot guarantee
   * that ordering — `main.tsx` sets the attribute synchronously before the first render for
   * exactly that reason. This effect owns the attribute afterwards, and restores it.
   */
  useEffect(() => {
    const html = document.documentElement;
    const previous = html.getAttribute('lang');
    html.setAttribute('lang', locale);

    return () => {
      if (previous === null) html.removeAttribute('lang');
      else html.setAttribute('lang', previous);
    };
  }, [locale]);

  /*
   * [sic] `scrollable: false` sets `overflow: auto` — i.e. it *enables* scrolling. The flag
   * reads backwards and it is not a typo in this port: it is what the legacy code does
   * (MIGRATION decision #7) and what the deployed rendering depends on. No deployed config
   * sets `scrollable` at all, so every live display takes this branch. Do not "fix" it
   * without a screen to test against.
   */
  useEffect(() => {
    if (scrollable) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'auto';

    return () => {
      document.body.style.overflow = previous;
    };
  }, [scrollable]);

  /*
   * `layout.background` is applied inline, so it beats the `body.bp` / `body.symbiosy-color`
   * rule from `global.css`. Four of the seven configs use both: class `bp` for the forest
   * photograph, then an inline gradient that covers it. Preserved as-is — the composition is
   * what is on the wall.
   */
  useEffect(() => {
    if (!background) return;
    const previous = document.body.style.background;
    document.body.style.background = background;

    return () => {
      document.body.style.background = previous;
    };
  }, [background]);

  useEffect(() => {
    if (!fontColor) return;
    const previous = document.body.style.color;
    document.body.style.color = fontColor;

    return () => {
      document.body.style.color = previous;
    };
  }, [fontColor]);

  /*
   * A full replace, not an append — legacy behaviour, and the configs rely on it. It is safe
   * because Tailwind puts no classes on `body`; if anything ever needs one, it belongs in a
   * `global.css` rule keyed off these theme classes, not on the element.
   */
  useEffect(() => {
    if (!bodyClass) return;
    const previous = document.body.className;
    document.body.className = bodyClass;

    return () => {
      document.body.className = previous;
    };
  }, [bodyClass]);
}
